import type {
  BudgetRaise,
  BudgetUse,
  BudgetVersion,
  ProjectBudget,
  RaiseKind,
} from "@cloudserver/contracts";
import {
  BudgetAmount,
  BudgetThresholds,
  ChangeMargin,
  DEFAULT_THRESHOLDS,
} from "@cloudserver/contracts";
import clsx from "clsx";
import { Check, HandCoins, PiggyBank, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  useDecideRaise,
  useProjectBudget,
  useRequestRaise,
  useSetBudget,
} from "../../api/budgets.js";
import { useCanWrite, useIsAdmin } from "../../api/users.js";
import { ErrorMessage } from "../../ui/feedback.js";
import { dateTime, withUnit } from "../../ui/format.js";
import { Section } from "../../ui/layout.js";
import { Dialog, type MenuItem } from "../../ui/overlay.js";
import { Badge, Button, Card, Select, TextArea, TextField } from "../../ui/primitives.js";
import { DataTable } from "../../ui/table.js";
import { useChangeRunner } from "../users/shared.js";
import { useStepUpGuard } from "../users/step-up.js";
import { Tile, useMoney } from "./shared.js";

/*
 * A project's budget (docs/budgets.md) on its costs page: amount, margin,
 * thresholds, how much is used, the two rules, its history per month, this month's alerts and the raise
 * requests. Administrators set it and decide raises (with a second-factor
 * confirmation); project owners ask for a raise; guests read.
 */

/** A bar of how much of the budget is used; past 100 % it turns red. */
export function UseBar({ percent, label }: { percent: number; label: string }) {
  return (
    <span
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.min(100, Math.round(percent))}
      className="block h-2 overflow-hidden rounded-full bg-surface-2"
    >
      <span
        className={clsx(
          "block h-full rounded-full",
          percent >= 100 ? "bg-danger" : percent >= 80 ? "bg-warn" : "bg-accent",
        )}
        style={{ width: `${Math.min(100, percent)}%` }}
      />
    </span>
  );
}

/** Month to date and forecast against the budget, as two labelled bars. */
export function BudgetUseBars({ use, currency }: { use: BudgetUse; currency: string }) {
  const { t } = useTranslation();
  const fmt = useMoney(currency);
  const pct = (value: number) => withUnit(new Intl.NumberFormat().format(value), "%");
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex flex-col gap-1">
        <span className="flex justify-between gap-3">
          <span className="text-muted">{t("budgets.used")}</span>
          <span className="tabular-nums">{pct(use.usedPercent)}</span>
        </span>
        <UseBar percent={use.usedPercent} label={t("budgets.used")} />
      </div>
      {use.forecastPercent !== null && (
        <div className="flex flex-col gap-1">
          <span className="flex justify-between gap-3">
            <span className="text-muted">{t("budgets.forecastUse")}</span>
            <span className="tabular-nums">{pct(use.forecastPercent)}</span>
          </span>
          <UseBar percent={use.forecastPercent} label={t("budgets.forecastUse")} />
        </div>
      )}
      <p className="text-xs text-muted">
        {t("budgets.ofBudget", { amount: fmt(use.amount) })}
        {Number(use.raised) > 0 &&
          ` · ${t("budgets.raisedThisMonth", { amount: fmt(use.raised) })}`}
      </p>
    </div>
  );
}

/** The budget among the cost tiles: its amount and the forecast's share of it. */
export function BudgetTile({ use, currency }: { use: BudgetUse; currency: string }) {
  const { t } = useTranslation();
  const fmt = useMoney(currency);
  const percent = use.forecastPercent ?? use.usedPercent;
  return (
    <Tile label={t("budgets.title")} value={fmt(use.amount)}>
      <UseBar percent={percent} label={t("budgets.forecastUse")} />
      <span className="text-xs text-muted">
        {t(use.forecastPercent === null ? "budgets.usedShort" : "budgets.forecastShort", {
          percent: Math.round(percent),
        })}
      </span>
    </Tile>
  );
}

/** Every version of the budget: each month is judged by the version it had. */
function BudgetHistory({ history, currency }: { history: BudgetVersion[]; currency: string }) {
  const { t, i18n } = useTranslation();
  const fmt = useMoney(currency);
  const monthName = (key: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(`${key}-01T00:00:00Z`));
  return (
    <div className="flex flex-col gap-1 border-t border-line pt-3 text-sm">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted">
        {t("budgets.history")}
      </h3>
      <ul className="flex flex-col gap-1">
        {history.map((v) => (
          <li key={v.validFrom} className="flex flex-wrap justify-between gap-x-3">
            <span>{t("budgets.historyFrom", { month: monthName(v.validFrom) })}</span>
            <span className="tabular-nums text-muted">
              {v.monthlyAmount === null
                ? t("budgets.noneShort")
                : `${fmt(v.monthlyAmount)}${v.changeMargin === null ? "" : ` · ${t("budgets.marginShort", { margin: v.changeMargin })}`}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const STATUS_TONE = { pending: "warn", approved: "ok", rejected: "neutral" } as const;

/** The budget section of a project's costs page. */
export function BudgetSection({
  projectId,
  openRaise = false,
}: {
  projectId: string;
  /** Opens the raise request at once (the link of a refused change). */
  openRaise?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const admin = useIsAdmin();
  const canWrite = useCanWrite(projectId);
  const budget = useProjectBudget(projectId);
  const [dialog, setDialog] = useState<
    { kind: "set" } | { kind: "raise" } | { kind: "decide"; raise: BudgetRaise } | null
  >(openRaise ? { kind: "raise" } : null);
  const { guard, dialog: stepUp } = useStepUpGuard();
  const fmt = useMoney(budget.data?.currency);
  const close = () => setDialog(null);
  if (budget.error) return <ErrorMessage error={budget.error} />;
  if (!budget.data) return null;
  const b = budget.data;
  const ownerMayAsk = canWrite && !admin && b.budget !== null;

  const actions = (
    <div className="flex flex-wrap gap-2">
      {admin && (
        <Button
          variant="secondary"
          icon={<PiggyBank className="size-4" aria-hidden />}
          onClick={() => setDialog({ kind: "set" })}
        >
          {b.budget ? t("budgets.change") : t("budgets.set")}
        </Button>
      )}
      {ownerMayAsk && (
        <Button
          variant="secondary"
          icon={<HandCoins className="size-4" aria-hidden />}
          onClick={() => setDialog({ kind: "raise" })}
        >
          {t("budgets.requestRaise")}
        </Button>
      )}
    </div>
  );

  const raiseMenu = (r: BudgetRaise): MenuItem[][] => [
    [
      {
        label: t("budgets.decide"),
        icon: <Check className="size-4" aria-hidden />,
        disabled: r.status !== "pending",
        reason: t("budgets.decided"),
        onSelect: () => setDialog({ kind: "decide", raise: r }),
      },
    ],
  ];

  return (
    <Section title={t("budgets.title")} description={t("budgets.lead")} actions={actions}>
      <Card className="flex flex-col gap-4">
        {b.budget && b.use ? (
          <div className="grid gap-4 md:grid-cols-[1fr_2fr]">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted">{t("budgets.monthly")}</dt>
              <dd className="font-semibold tabular-nums">{fmt(b.use.monthlyAmount)}</dd>
              <dt className="text-muted">{t("budgets.margin")}</dt>
              <dd>
                {t("budgets.marginValue", {
                  margin: b.use.changeMargin,
                  limit: fmt(b.use.changeLimit),
                })}
                <span className="block text-xs text-muted">
                  {b.budget.changeMargin === null
                    ? t("budgets.marginDefault")
                    : t("budgets.marginOwn")}
                </span>
              </dd>
              <dt className="text-muted">{t("budgets.thresholds")}</dt>
              <dd>{b.budget.thresholds.map((v) => withUnit(v, "%")).join(", ")}</dd>
            </dl>
            <div className="flex flex-col gap-3">
              {b.use.exceeded && <Badge tone="danger">{t("budgets.exceededBadge")}</Badge>}
              <BudgetUseBars use={b.use} currency={b.currency} />
            </div>
            <ul className="flex list-disc flex-col gap-1 pl-5 text-xs text-muted md:col-span-2">
              <li>{t("budgets.ruleNew")}</li>
              <li>{t("budgets.ruleChange", { margin: b.use.changeMargin })}</li>
              <li>{t("budgets.ruleNothing")}</li>
            </ul>
          </div>
        ) : (
          <p className="text-sm text-muted">{t("budgets.none")}</p>
        )}
        {b.history.length > 0 && <BudgetHistory history={b.history} currency={b.currency} />}
        {b.alerts.length > 0 && (
          <ul className="flex flex-col gap-1 border-t border-line pt-3 text-sm">
            {b.alerts.map((a) => (
              <li key={`${a.basis}:${a.threshold}`} className="flex justify-between gap-3">
                <span>{t(`budgets.alert.${a.basis}`, { threshold: a.threshold })}</span>
                <span className="shrink-0 text-xs text-muted">
                  {dateTime(a.sentAt, i18n.language)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {b.raises.length > 0 && (
        <DataTable<BudgetRaise>
          label={t("budgets.raises")}
          rows={b.raises}
          rowKey={(r) => r.id}
          columns={[
            {
              key: "amount",
              header: t("budgets.raiseAmount"),
              primary: true,
              cell: (r) => (
                <span className="flex flex-col">
                  <span className="font-medium tabular-nums">+{fmt(r.amount)}</span>
                  <span className="text-xs text-muted">{t(`budgets.kinds.${r.kind}`)}</span>
                </span>
              ),
            },
            { key: "reason", header: t("budgets.reason"), cell: (r) => r.reason },
            {
              key: "by",
              header: t("budgets.requestedBy"),
              cell: (r) => (
                <span className="flex flex-col">
                  <span>{r.requestedBy?.name ?? "—"}</span>
                  <span className="text-xs text-muted">
                    {dateTime(r.requestedAt, i18n.language)}
                  </span>
                </span>
              ),
            },
            {
              key: "status",
              header: t("budgets.status"),
              cell: (r) => (
                <span className="flex flex-col gap-0.5">
                  <Badge tone={STATUS_TONE[r.status]}>{t(`budgets.statuses.${r.status}`)}</Badge>
                  {r.note && <span className="text-xs text-muted">{r.note}</span>}
                </span>
              ),
            },
          ]}
          {...(admin ? { rowMenu: raiseMenu } : {})}
        />
      )}
      {dialog?.kind === "set" && (
        <BudgetDialog projectId={projectId} budget={b} guard={guard} onClose={close} />
      )}
      {dialog?.kind === "raise" && (ownerMayAsk || openRaise) && (
        <RaiseDialog projectId={projectId} budget={b} onClose={close} />
      )}
      {dialog?.kind === "decide" && (
        <DecideDialog raise={dialog.raise} guard={guard} onClose={close} />
      )}
      {stepUp}
    </Section>
  );
}

type Guard = <T>(change: () => Promise<T>) => Promise<T>;

/** Sets, changes or removes the budget (administrators, with a step-up). */
function BudgetDialog({
  projectId,
  budget,
  guard,
  onClose,
}: {
  projectId: string;
  budget: ProjectBudget;
  guard: Guard;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const run = useChangeRunner();
  const set = useSetBudget(projectId);
  const [amount, setAmount] = useState(
    budget.budget ? String(Number(budget.budget.monthlyAmount)) : "",
  );
  // Empty: the organisation's default margin.
  const [margin, setMargin] = useState(
    budget.budget?.changeMargin === null || budget.budget?.changeMargin === undefined
      ? ""
      : String(budget.budget.changeMargin),
  );
  const [thresholds, setThresholds] = useState(
    (budget.budget?.thresholds ?? [...DEFAULT_THRESHOLDS]).join(", "),
  );
  const parsedMargin =
    margin.trim() === ""
      ? ({ success: true, data: null } as const)
      : ChangeMargin.safeParse(Number(margin.trim()));
  const parsedAmount = BudgetAmount.safeParse(amount.replace(",", "."));
  const parsedThresholds = BudgetThresholds.safeParse(
    thresholds
      .split(/[,\s]+/)
      .filter(Boolean)
      .map(Number),
  );
  const valid = parsedAmount.success && parsedThresholds.success && parsedMargin.success;

  const save = (monthlyAmount: string | null) =>
    void run(
      () =>
        guard(() =>
          set.mutateAsync(
            monthlyAmount === null
              ? { monthlyAmount: null }
              : {
                  monthlyAmount,
                  changeMargin: parsedMargin.success ? parsedMargin.data : null,
                  thresholds: parsedThresholds.data ?? [],
                },
          ),
        ),
      monthlyAmount === null ? t("budgets.removed") : t("budgets.saved"),
    ).then((done) => done && onClose());

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (parsedAmount.success) save(parsedAmount.data);
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={budget.budget ? t("budgets.change") : t("budgets.set")}
      description={t("budgets.dialogLead")}
      footer={
        <>
          {budget.budget && (
            <Button variant="danger" onClick={() => save(null)} busy={set.isPending}>
              {t("budgets.remove")}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="budget-form" busy={set.isPending} disabled={!valid}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form id="budget-form" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("budgets.amountLabel", { currency: budget.currency })}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          hint={t("budgets.amountHint")}
          error={amount && !parsedAmount.success ? t("budgets.amountInvalid") : undefined}
        />
        <TextField
          label={t("budgets.marginLabel")}
          inputMode="numeric"
          value={margin}
          onChange={(e) => setMargin(e.target.value)}
          placeholder={String(budget.defaultChangeMargin)}
          hint={t("budgets.marginHint", { margin: budget.defaultChangeMargin })}
          error={parsedMargin.success ? undefined : t("budgets.marginInvalid")}
        />
        <TextField
          label={t("budgets.thresholds")}
          value={thresholds}
          onChange={(e) => setThresholds(e.target.value)}
          hint={t("budgets.thresholdsHint")}
          error={parsedThresholds.success ? undefined : t("budgets.thresholdsInvalid")}
        />
        <ErrorMessage error={set.error} />
      </form>
    </Dialog>
  );
}

/** A project owner asks for more: this month only, or from now on. */
function RaiseDialog({
  projectId,
  budget,
  onClose,
}: {
  projectId: string;
  budget: ProjectBudget;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const run = useChangeRunner();
  const ask = useRequestRaise(projectId);
  const [amount, setAmount] = useState("");
  const [kind, setKind] = useState<RaiseKind>("once");
  const [reason, setReason] = useState("");
  const parsed = BudgetAmount.safeParse(amount.replace(",", "."));
  const valid = parsed.success && reason.trim() !== "";

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!parsed.success) return;
    void run(
      () => ask.mutateAsync({ amount: parsed.data, kind, reason }),
      t("budgets.requested"),
    ).then((done) => done && onClose());
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("budgets.requestRaise")}
      description={t("budgets.raiseLead")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="raise-form" busy={ask.isPending} disabled={!valid}>
            {t("budgets.send")}
          </Button>
        </>
      }
    >
      <form id="raise-form" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("budgets.raiseBy", { currency: budget.currency })}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={amount && !parsed.success ? t("budgets.amountInvalid") : undefined}
        />
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          {t("budgets.raiseKind")}
          <Select
            label={t("budgets.raiseKind")}
            value={kind}
            onChange={(e) => setKind(e.target.value as RaiseKind)}
          >
            <option value="once">{t("budgets.kinds.once")}</option>
            <option value="permanent">{t("budgets.kinds.permanent")}</option>
          </Select>
        </label>
        <TextArea
          label={t("budgets.reason")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
        />
        <ErrorMessage error={ask.error} />
      </form>
    </Dialog>
  );
}

/** An administrator approves or rejects a raise, with a note and a step-up. */
export function DecideDialog({
  raise,
  guard,
  onClose,
}: {
  raise: BudgetRaise;
  guard: Guard;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const run = useChangeRunner();
  const decide = useDecideRaise();
  const [note, setNote] = useState("");
  const fmt = useMoney(undefined);
  const go = (decision: "approve" | "reject") =>
    void run(
      () => guard(() => decide.mutateAsync({ id: raise.id, decision, ...(note ? { note } : {}) })),
      decision === "approve" ? t("budgets.approved") : t("budgets.rejected"),
    ).then((done) => done && onClose());
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("budgets.decideTitle", { project: raise.projectName })}
      description={t("budgets.decideLead", {
        amount: fmt(raise.amount),
        kind: t(`budgets.kinds.${raise.kind}`),
        name: raise.requestedBy?.name ?? "—",
      })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="danger"
            icon={<X className="size-4" aria-hidden />}
            busy={decide.isPending}
            onClick={() => go("reject")}
          >
            {t("budgets.reject")}
          </Button>
          <Button
            icon={<Check className="size-4" aria-hidden />}
            busy={decide.isPending}
            onClick={() => go("approve")}
          >
            {t("budgets.approve")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <blockquote className="rounded-lg bg-surface-2 px-4 py-3 text-sm">
          {raise.reason}
        </blockquote>
        <TextArea
          label={t("budgets.note")}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          maxLength={500}
        />
        <ErrorMessage error={decide.error} />
      </div>
    </Dialog>
  );
}
